import {
  useSiteStrips,
  useApiClient,
  useDismissSiteStrip,
  type SiteStripAd,
} from '@weather-story/shared-hooks';
import { ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';

interface WeatherSiteStripProps {
  temperature?: number;
  conditions?: string;
  precipitation?: number;
  maxProducts?: number;
  compact?: boolean; // Single product inline mode for stories
  productIndex?: number; // Which product to show in compact mode (prevents repeats)
  priceRange?: 'impulse' | 'premium' | 'all'; // Filter by price
}

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Parse price string to number (e.g., "$59" -> 59, "$2,999" -> 2999)
function parsePrice(priceStr: string | undefined): number {
  if (!priceStr) return 0;
  const cleaned = priceStr.replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
}

// Filter products by price range
function filterByPriceRange(
  products: SiteStripAd[],
  range: 'impulse' | 'premium' | 'all'
): SiteStripAd[] {
  if (range === 'all') return products;

  return products.filter((product) => {
    const price = parsePrice(product.price ?? undefined);
    if (range === 'impulse') return price < 30; // Impulse buys: < $30
    if (range === 'premium') return price >= 50; // Premium items: >= $50
    return true;
  });
}

/**
 * Mini image gallery for product images.
 * Shows main image with clickable thumbnails below.
 */
function ProductImageGallery({
  images,
  alt,
  className = '',
  compact = false,
}: {
  images: string[];
  alt: string;
  className?: string;
  compact?: boolean;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const displayImages = images.length > 0 ? images : [];

  const handleNext = () => {
    setActiveIndex((i) => (i + 1) % displayImages.length);
  };

  // Auto-rotate every 3s if more than 1 image
  useEffect(() => {
    if (displayImages.length <= 1) return;
    const timer = setInterval(handleNext, 3000);
    return () => clearInterval(timer);
  }, [displayImages.length, handleNext]);

  if (displayImages.length === 0) return null;

  return (
    <div className={className}>
      <img
        src={displayImages[activeIndex]}
        alt={alt}
        className={`w-full ${compact ? 'h-20' : 'h-24'} rounded-lg object-contain bg-gray-900`}
        loading="lazy"
      />
      {displayImages.length > 1 && (
        <div className="flex gap-1 mt-1.5 overflow-x-auto">
          {displayImages.slice(0, 6).map((url, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`flex-shrink-0 w-8 h-8 rounded border transition-all ${
                i === activeIndex
                  ? 'border-purple-400 ring-1 ring-purple-400/50'
                  : 'border-gray-700 hover:border-gray-500'
              }`}
            >
              <img
                src={url}
                alt=""
                className="w-full h-full object-contain rounded bg-gray-900"
                loading="lazy"
              />
            </button>
          ))}
          {displayImages.length > 6 && (
            <span className="flex-shrink-0 w-8 h-8 rounded border border-gray-700 flex items-center justify-center text-[10px] text-gray-500">
              +{displayImages.length - 6}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Weather-aware affiliate product display.
 * Shows multiple products relevant to current weather conditions.
 * Auto-scrolls every 5 seconds, pauses on hover.
 * Compact mode shows single product inline with content.
 * Price range filtering: impulse (< $30) vs premium (>= $50).
 */
export function WeatherSiteStrip({
  temperature,
  conditions,
  precipitation,
  maxProducts = 3,
  compact = false,
  productIndex = 0,
  priceRange = 'all',
}: WeatherSiteStripProps) {
  const apiClient = useApiClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const { mutate: dismissProduct } = useDismissSiteStrip();

  // Random seed per mount — forces a fresh shuffle every time the component mounts
  const [shuffleSeed] = useState(() => Math.random());

  const { data, isLoading } = useSiteStrips({
    temp: temperature,
    conditions,
    precipitation,
    limit: maxProducts,
  });

  // Filter by price range and dismissed; shuffle for strip mode, stable order for compact (index-based)
  const filteredProducts = useMemo(() => {
    if (!data?.data) return [];
    return filterByPriceRange(data.data, priceRange).filter((p) => !dismissedIds.has(p.id));
  }, [data?.data, priceRange, dismissedIds]);

  const randomizedProducts = useMemo(() => {
    if (compact) return filteredProducts; // Stable order so productIndex doesn't repeat
    void shuffleSeed; // New random seed per mount triggers fresh shuffle
    return shuffleArray(filteredProducts);
  }, [filteredProducts, compact, shuffleSeed]);

  const handleClick = async (id: string, url: string) => {
    // Track click
    try {
      await apiClient.post(`/sitestrips/${id}/click`, {});
    } catch (err) {
      console.warn('Failed to track click:', err);
    }
    // Open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDismiss = (id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id));
    dismissProduct(id);
  };

  const updateScrollButtons = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 320; // Width of one card + gap
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // Auto-scroll functionality - scrolls to next product every 5 seconds
  useEffect(() => {
    if (!scrollRef.current || randomizedProducts.length <= 1 || isHovering) return;

    autoScrollInterval.current = setInterval(() => {
      if (!scrollRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 10;

      if (isAtEnd) {
        // Scroll back to start
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        // Scroll to next product
        scroll('right');
      }
    }, 5000); // 5 seconds

    return () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, [randomizedProducts, isHovering]);

  // Update scroll button states
  useEffect(() => {
    updateScrollButtons();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        ref.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [randomizedProducts]);

  if (isLoading || !randomizedProducts || randomizedProducts.length === 0) {
    return null;
  }

  const products = randomizedProducts;

  // Compact mode: single product card matching story card size
  if (compact && products.length > 0) {
    const product = products[productIndex % products.length];
    return (
      <div className="relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-purple-500/40 transition-all duration-300 h-full flex flex-col">
        <button
          onClick={() => handleDismiss(product.id)}
          className="absolute top-2 right-2 z-10 p-0.5 rounded-full text-gray-500 hover:text-gray-300 hover:bg-gray-700 transition-colors"
          title="Not for me"
          aria-label="Dismiss product"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="p-4 flex flex-col flex-1">
          {/* Header — matches story card badge style */}
          <div className="flex items-center justify-between mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500">
              Sponsored
            </span>
            {product.price && (
              <span className="text-sm font-bold text-purple-300">{product.price}</span>
            )}
          </div>

          {/* Product images */}
          {(product.imageUrls?.length > 0 || product.imageUrl) && (
            <ProductImageGallery
              images={
                product.imageUrls?.length > 0
                  ? product.imageUrls
                  : product.imageUrl
                    ? [product.imageUrl]
                    : []
              }
              alt={product.title}
              className="mb-3"
              compact
            />
          )}

          {/* Title & description */}
          <h3 className="font-display font-semibold text-gray-100 text-base mb-2 line-clamp-2">
            {product.title}
          </h3>
          {product.description && (
            <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 mb-auto">
              {product.description}
            </p>
          )}

          {/* Footer — matches story card footer style */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
            <p className="text-[10px] text-gray-500">Affiliate link</p>
            <button
              onClick={() => handleClick(product.id, product.affiliateUrl)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-500 transition-colors"
            >
              {product.ctaText}
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full strip mode: carousel with multiple products
  return (
    <div
      className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-4"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-purple-400 font-medium">Recommended for You</p>
        {products.length > 1 && (
          <div className="flex gap-1">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="p-1 rounded-lg bg-purple-900/30 text-purple-400 hover:bg-purple-900/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="p-1 rounded-lg bg-purple-900/30 text-purple-400 hover:bg-purple-900/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => (
          <div
            key={product.id}
            className="relative flex-shrink-0 w-[300px] bg-gray-800/50 rounded-lg p-3 snap-start border border-purple-500/20 hover:border-purple-400/40 transition-colors"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss(product.id);
              }}
              className="absolute top-2 right-2 z-10 p-0.5 rounded-full text-gray-500 hover:text-gray-300 hover:bg-gray-700 transition-colors"
              title="Not for me"
              aria-label="Dismiss product"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="flex gap-3">
              {(product.imageUrls?.length > 0 || product.imageUrl) && (
                <div className="w-20 flex-shrink-0">
                  <img
                    src={product.imageUrls?.[0] ?? product.imageUrl ?? ''}
                    alt={product.title}
                    className="w-20 h-20 rounded-lg object-cover bg-gray-900"
                    loading="lazy"
                  />
                  {product.imageUrls && product.imageUrls.length > 1 && (
                    <p className="text-[9px] text-gray-500 text-center mt-0.5">
                      {product.imageUrls.length} photos
                    </p>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-100 line-clamp-2 mb-1">
                  {product.title}
                </h3>
                {product.price && (
                  <p className="text-lg font-bold text-purple-300 mb-2">{product.price}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleClick(product.id, product.affiliateUrl)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-500 transition-colors mt-3"
            >
              {product.ctaText}
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-gray-500 mt-3">Sponsored • Affiliate links</p>
    </div>
  );
}
